import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Switch, Route, Redirect, NavLink, useRouteMatch } from 'react-router-dom';

import axios from 'axios';

import Loading from '../../../components/common/loading';
import TextInput from '../../../components/common/inputs/textInput.jsx';
import ToggleInput from '../../../components/common/inputs/toggleInput.jsx';
import SelectInput from '../../../components/common/inputs/selectInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import configurationSections from '../configurationSections';
import DeviceConfigurationUsers from './deviceConfigurationUsers';

const styles = {
  container: {
    padding: '24px',
  },
  header: {
    marginBottom: '24px',
  },
  headerTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  headerSubtitle: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 260px) 1fr',
    gap: '24px',
    alignItems: 'flex-start',
  },
  sidebar: {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  sidebarList: {
    listStyle: 'none',
    margin: 0,
    padding: '8px 0',
  },
  sidebarLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 18px',
    color: '#4b5563',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background 0.2s ease, color 0.2s ease',
  },
  sidebarLinkActive: {
    color: '#1f2d3d',
    background: 'rgba(59, 130, 246, 0.16)',
    borderLeft: '3px solid #3b82f6',
  },
  sidebarLinkIcon: {
    fontSize: '18px',
    color: 'inherit',
  },
  content: {
    minHeight: '320px',
  },
  detailWrap: {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '24px',
  },
  detailLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '220px',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#7f8c8d',
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  detailHeader: {
    marginBottom: '8px',
  },
  detailTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#1f2d3d',
    fontWeight: 600,
  },
  detailDescription: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  permissionBanner: {
    marginBottom: '16px',
    padding: '12px 16px',
    background: '#f1f5f9',
    borderRadius: 6,
    color: '#475569',
    fontSize: '13px',
  },
  formSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #f1f5f9',
  },
  firstFormSection: {
    borderTop: 'none',
    paddingTop: 0,
  },
  formSectionHeader: {
    marginBottom: '16px',
  },
  formSectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  formSectionDescription: {
    marginTop: '6px',
    fontSize: '13px',
    color: '#64748b',
    lineHeight: 1.6,
  },
  formFields: {
    display: 'grid',
    rowGap: '18px',
  },
  fieldWrapper: {
    background: '#f9fafb',
    borderRadius: 8,
    padding: '18px 20px',
  },
  actions: {
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  errorText: {
    color: '#b91c1c',
    fontSize: '13px',
  },
};

const removeTrailingSlash = (value = '') => (value.endsWith('/') ? value.slice(0, -1) : value);

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
};

const DeviceConfigurationSectionForm = ({
  section,
  device,
  conf,
  loading,
  refreshConf,
  user,
  deviceOffline,
  offlineReason,
}) => {
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const context = useMemo(() => ({ conf, device }), [conf, device]);

  const fields = useMemo(() => {
    return (section.formSections || []).flatMap((group) => group.fields || []);
  }, [section]);

  const initialValues = useMemo(() => {
    const defaults = {};
    fields.forEach((field) => {
      let value;
      if (field.getInitialValue) {
        value = field.getInitialValue(context);
      } else if (field.type === 'toggle') {
        value = normalizeBoolean(conf?.[field.name]);
      } else if (field.type === 'select') {
        const raw = conf?.[field.name];
        value = raw === undefined || raw === null ? '' : String(raw);
      } else {
        value = conf?.[field.name] ?? '';
      }

      if (field.type === 'toggle') {
        defaults[field.name] = normalizeBoolean(value);
      } else if (field.type === 'select') {
        defaults[field.name] = value ?? '';
      } else if (field.type === 'number' && value !== '' && value !== null && value !== undefined) {
        defaults[field.name] = Number(value);
      } else {
        defaults[field.name] = value ?? '';
      }
    });
    return defaults;
  }, [fields, context, conf]);

  useEffect(() => {
    setFormValues(initialValues);
    setError('');
  }, [initialValues]);

  const hasEditPermission = user?.role?.editDeviceInfo !== false;
  const offline = !!deviceOffline;
  const canEdit = hasEditPermission && !offline;

  const handleChange = useCallback(
    (fieldName, value) => {
      setFormValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      setError('');
    },
    [setError]
  );

  const isDirty = useMemo(() => {
    return fields.some((field) => {
      const name = field.name;
      const current = formValues[name];
      const initial = initialValues[name];
      if (field.compare) {
        return !field.compare(current, initial, {
          formValues,
          initialValues,
          conf,
          device,
        });
      }
      return current !== initial;
    });
  }, [fields, formValues, initialValues, conf, device]);

  const deviceSerial = conf?.enclosureSerialNumber || device?.enclosureSerialNumber;

  const handleReset = useCallback(() => {
    setFormValues(initialValues);
    setError('');
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    if (!canEdit || saving || !deviceSerial) {
      return;
    }

    const payload = {};
    fields.forEach((field) => {
      const name = field.name;
      const current = formValues[name];
      const initial = initialValues[name];

      const hasChanged = field.compare
        ? !field.compare(current, initial, {
            formValues,
            initialValues,
            conf,
            device,
          })
        : current !== initial;

      if (!hasChanged) {
        return;
      }

      const valueToTransform = current;
      const transformed = field.transformOut
        ? field.transformOut(valueToTransform, { formValues, conf, device })
        : valueToTransform;

      if (transformed === undefined) {
        return;
      }

      payload[field.payloadKey || name] = transformed;
    });

    if (!Object.keys(payload).length) {
      setError('No changes detected for this section.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      await axios.put(`/v1/conf/${deviceSerial}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      NotificationManager.success('Configuration updated successfully.');
      if (refreshConf) {
        refreshConf({ useCache: false, force: true });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update configuration. Please try again.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setSaving(false);
    }
  }, [
    canEdit,
    conf,
    device,
    deviceSerial,
    fields,
    formValues,
    initialValues,
    refreshConf,
    saving,
  ]);

  if (loading) {
    return (
      <div style={{ ...styles.detailWrap, ...styles.detailLoading }}>
        <Loading size="32px" />
      </div>
    );
  }

  if (!conf) {
    return (
      <div style={styles.emptyState}>
        Configuration data will appear once the latest device configuration finishes loading.
      </div>
    );
  }

  return (
    <div style={styles.detailWrap}>
      <div style={styles.detailHeader}>
        <h4 style={styles.detailTitle}>{section.label}</h4>
        <p style={styles.detailDescription}>{section.description}</p>
      </div>
      {!canEdit && (
        <div style={styles.permissionBanner}>
          {offline
            ? offlineReason ||
              'Device is offline. Configuration changes are disabled until it reconnects.'
            : 'You can view this configuration, but your role does not allow editing device settings.'}
        </div>
      )}
      {(section.formSections || []).map((group, index) => (
        <section
          key={group.title || `${section.key}-group-${index}`}
          style={{
            ...styles.formSection,
            ...(index === 0 ? styles.firstFormSection : {}),
          }}
        >
          <header style={styles.formSectionHeader}>
            <h5 style={styles.formSectionTitle}>{group.title}</h5>
            {group.description ? (
              <p style={styles.formSectionDescription}>{group.description}</p>
            ) : null}
          </header>
          <div style={styles.formFields}>
            {(group.fields || []).map((field) => {
              const fieldValue = formValues[field.name];
              const computedDisabled =
                !canEdit ||
                saving ||
                (typeof field.disabled === 'function'
                  ? field.disabled(formValues)
                  : field.disabled);

              let input = null;
              if (field.type === 'toggle') {
                input = (
                  <ToggleInput
                    label={field.label}
                    value={normalizeBoolean(fieldValue)}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    toggleLabelOff={field.toggleLabelOff || 'Off'}
                    toggleLabelOn={field.toggleLabelOn || 'On'}
                    description={field.description}
                    id={`${section.key}-${field.name}`}
                  />
                );
              } else if (field.type === 'select') {
                input = (
                  <SelectInput
                    label={field.label}
                    value={fieldValue ?? ''}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    options={field.options || []}
                    defaultSelectText={field.defaultSelectText || 'Select option'}
                    description={field.description}
                  />
                );
              } else {
                const inputType = field.type === 'password' ? 'password' : field.type || 'text';
                input = (
                  <TextInput
                    label={field.label}
                    value={fieldValue ?? ''}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    placeholder={field.placeholder}
                    type={inputType}
                    min={field.min}
                    max={field.max}
                    description={field.description}
                    inputContainerStyle={{ marginBottom: 0 }}
                  />
                );
              }

              return (
                <div key={field.name} style={styles.fieldWrapper}>
                  {input}
                </div>
              );
            })}
          </div>
        </section>
      ))}
      <div style={styles.actions}>
        <button
          type="button"
          className="button"
          onClick={handleSubmit}
          disabled={!canEdit || !isDirty || saving || !deviceSerial}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button
          type="button"
          className="button secondary"
          onClick={handleReset}
          disabled={!canEdit || saving || !isDirty}
        >
          Reset
        </button>
        {error ? <span style={styles.errorText}>{error}</span> : null}
      </div>
    </div>
  );
};

export default function DevicePageConfiguration({ device, conf, confLoading, refreshConf, user, deviceOffline, offlineReason }) {
  const match = useRouteMatch();
  const baseUrl = removeTrailingSlash(match?.url || '');
  const basePath = removeTrailingSlash(match?.path || '');

  const navSections = configurationSections.map((section) => ({
    ...section,
    path: `${baseUrl}/${section.key}`,
  }));

  const defaultPath = navSections[0]?.path || baseUrl;
  const customComponents = useMemo(
    () => ({
      DeviceUsers: DeviceConfigurationUsers,
    }),
    []
  );

  return (
    <div style={styles.container}>
      {/* {deviceOffline ? (
        <div className="callout warning" style={{ marginBottom: '16px' }}>
          {offlineReason || 'Device is offline. Configuration changes are temporarily unavailable.'}
        </div>
      ) : null} */}
      <div style={styles.layout}>
        <nav style={styles.sidebar}>
          <ul style={styles.sidebarList}>
            {navSections.map((section) => (
              <li key={section.path}>
                <NavLink
                  to={section.path}
                  style={styles.sidebarLink}
                  activeStyle={styles.sidebarLinkActive}
                >
                  <i className="material-icons" style={styles.sidebarLinkIcon}>
                    {section.icon}
                  </i>
                  <span>{section.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div style={styles.content}>
          <Switch>
            <Route exact path={basePath}>
              <Redirect to={defaultPath} />
            </Route>
            {configurationSections.map((section) => {
              const CustomComponent = section.customComponent
                ? customComponents[section.customComponent]
                : null;

              return (
                <Route key={section.key} path={`${basePath}/${section.key}`}>
                  {CustomComponent ? (
                    <CustomComponent
                      section={section}
                      device={device}
                      conf={conf}
                      confLoading={confLoading}
                      refreshConf={refreshConf}
                      user={user}
                      deviceOffline={deviceOffline}
                      offlineReason={offlineReason}
                    />
                  ) : (
                    <DeviceConfigurationSectionForm
                      section={section}
                      device={device}
                      conf={conf}
                      loading={confLoading}
                      refreshConf={refreshConf}
                      user={user}
                      deviceOffline={deviceOffline}
                      offlineReason={offlineReason}
                    />
                  )}
                </Route>
              );
            })}
        </Switch>
      </div>
      </div>
    </div>
  );
}
